// src/components/admin/DecisionForm.jsx
// Editor decision page: read the manuscript + all reviewer reports, then
// compile the decision letter to the corresponding author.
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  FileText,
  Loader,
  User,
  Users,
  Tag,
  Paperclip,
  ClipboardCheck,
  Eye,
  AlertTriangle,
  ThumbsUp,
  ThumbsDown,
  Undo2,
  RefreshCw,
  X,
  UserPlus,
  Mail,
  Search
} from 'lucide-react';
import axios from '../../utils/axios';
import { showToast } from '../../utils/toast';
import { useAuth } from '../../context/AuthContext';
import RichTextViewer from '../common/RichTextViewer';
import RichTextEditor from '../common/RichTextEditor';
import uploadEditorImage from '../../utils/uploadEditorImage';
import { handleDownloadFile } from '../../utils/utils.global';
const SECTION1_SCORES = [
  'accurately_reflect_manuscript_subject_score',
  'clearly_summarize_content_score',
  'presents_what_is_known_score',
  'gives_accurate_summary_score',
  'purpose_clear_score',
  'method_section_clear_score',
  'study_materials_clearly_described_score',
  'research_method_valid_score',
  'ethical_standards_score',
  'study_find_clearly_described_score',
  'result_presented_logical_score',
  'graphics_complement_result_score',
  'table_follow_specified_standards_score',
  'tables_add_value_or_distract_score',
  'issues_with_title_score',
  'manuscript_present_summary_of_key_findings_score',
  'manuscript_highlight_strength_of_study_score',
  'manuscript_compare_findings_score',
  'manuscript_discuss_meaning_score',
  'manuscript_describes_overall_story_score',
  'conclusions_reflect_achievement_score',
  'manuscript_describe_gaps_score',
  'referencing_accurate_score'
];

const SECTION2_SCORES = [
  'novelty_score',
  'quality_score',
  'scientific_accuracy_score',
  'overall_merit_score',
  'english_level_score'
];

const FILE_LABELS = {
  manuscript_file: 'Manuscript',
  document_file: 'Document',
  tracked_manuscript_file: 'Tracked Manuscript',
  cover_letter_file: 'Cover Letter',
  tables: 'Tables',
  figures: 'Figures',
  graphic_abstract: 'Graphic Abstract',
  supplementary_material: 'Supplementary Material'
};

const getRecommendationColor = (recommendation) => {
  const colors = {
    'Accept': 'bg-green-100 text-green-800 border-green-200',
    'Minor Revision': 'bg-blue-100 text-blue-800 border-blue-200',
    'Major Revision': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'Reject': 'bg-red-100 text-red-800 border-red-200'
  };
  return colors[recommendation] || 'bg-gray-100 text-gray-800 border-gray-200';
};

const sumScores = (review, fields) =>
  fields.reduce((acc, field) => acc + (parseInt(review[field]) || 0), 0);

const DECISION_TEMPLATES = {
  accept: ({ manuscriptId, title, correspondingAuthor, editorName, editorDesignation, today }) => [
    { attributes: { color: '#e60000', bold: true }, insert: today },
    { insert: '\n\nDear ' },
    { attributes: { color: '#e60000', bold: true }, insert: correspondingAuthor },
    { insert: `,\n\nWe are pleased to accept your article "${title}" (${manuscriptId}) for publication in ASFI Research Journal (ASFIRJ).\n\nYour article will now be sent to ASFIRJ Production Office where copyediting and typesetting will be undertaken. The Production Office will email you a proof of the article to check within 2 weeks. Please check your junk mail folder if you did not receive such email within this time.\n\nThe proof is your opportunity to check for typesetting errors and the completeness and accuracy of the text, including author names and affiliations, tables and figures; including legends, numerical, mathematical, or other scientific expressions. We ask that you only make minor corrections at this stage.\n\nThe proof must be returned to the Production Office within 48 hours. There will be no further opportunities to make corrections prior to publication.\n\nIf your article is eligible for any article processing fee waiver, the final version of the accepted paper will undergo.\n\nIf your article is not eligible for waiver of the article processing fee (APF), within one week, you will receive an email with payment options and instructions from ASFIRJ Production Office to make your payment for the APF. Your article will not be processed by the Production Office until you have paid the APF.\n\nWithin one week of receiving your corrected article proof, the article will be published online on ASFIRJ website and you will receive an email notification in this regard and will subsequently be assigned to an issue in due course.\n\nThank you for your contribution to the journal. On behalf of the Editors of ASFIRJ, we look forward to your continued contributions to the Journal.\n\nSincerely,\n\n` },
    { attributes: { color: '#e60000', bold: true }, insert: editorName },
    { insert: '\n' },
    { attributes: { color: '#e60000', bold: true }, insert: editorDesignation },
    { insert: '\n\n' },
    { attributes: { link: 'mailto:submissions@asfirj.org' }, insert: 'submissions@asfirj.org' },
    { insert: '\nASFI Research Journal\nExcellence. Quality. Impact\n"Raising the bar of scientific publishing in Africa"\n' },
    { attributes: { link: 'https://asfirj.org/' }, insert: 'https://asfirj.org/' },
    { insert: '\n' },
    { attributes: { link: 'mailto:asfirj@asfirj.org' }, insert: 'asfirj@asfirj.org' },
    { insert: '\nLinkedIn: ' },
    { attributes: { link: 'http://www.linkedin.com/in/asfi-research-journal-1b9929309' }, insert: 'www.linkedin.com/in/asfi-research-journal-1b9929309' },
    { insert: '\nX (formerly Twitter): ' },
    { attributes: { link: 'https://twitter.com/asfirj1' }, insert: 'https://twitter.com/asfirj1' },
    { insert: '\nInstagram: ' },
    { attributes: { link: 'https://www.instagram.com/asfirj1/' }, insert: 'https://www.instagram.com/asfirj1/' },
    { insert: '\nWhatsApp: ' },
    { attributes: { link: 'https://chat.whatsapp.com/L8o0N0pUieOGIUHJ1hjSG3' }, insert: 'https://chat.whatsapp.com/L8o0N0pUieOGIUHJ1hjSG3' },
    { insert: '\n' }
  ],
  reject: ({ manuscriptId, title, correspondingAuthor, editorName, editorDesignation, today }) => [
    { insert: `${today}\n\n` },
    { insert: 'Dear ' },
    { attributes: { color: '#e60000', bold: true }, insert: correspondingAuthor },
    { insert: `,\n\nThank you for submitting your manuscript "${title}" (${manuscriptId}) to ASFI Research Journal (ASFIRJ), which has been evaluated by external reviewers and the editor.\n\nWe regret to inform you that your manuscript has not been accepted for publication in the ASFIRJ.\n\nIn addition to the criticism raised by the reviewers on your manuscript, the editorial judgment is that your manuscript did not reach a sufficient priority score that would warrant publication in ASFIRJ. It was, therefore, rejected after external revision.\n\nWhen evaluating whether a manuscript can be accepted for publication in ASFIRJ, the editors must consider several aspects of a submitted work, including the scientific value of the manuscript and the interest to the readers. Other aspects considered in reaching a decision on a manuscript are:\n\n•\tThe strength of the data used\n•\tNovelty of the concept\n•\tMethodological and experimental approach used\n•\tResults and appropriateness of presentation\n•\tScope of the manuscript and general interest of our readers\n•\tLevel of the presented evidence for the main findings and soundness of their discussion\n\nWe regret having to send you a negative decision on this occasion. We appreciate the time and effort you have expended in carrying out your study and giving us the opportunity to review it. We welcome the opportunity to review material from you and your colleagues in the future.\n\nSincerely,\n\n` },
    { attributes: { color: '#e60000', bold: true }, insert: editorName },
    { insert: '\n' },
    { attributes: { color: '#e60000', bold: true }, insert: editorDesignation },
    { insert: '\n\n' },
    { attributes: { link: 'mailto:submissions@asfirj.org' }, insert: 'submissions@asfirj.org' },
    { insert: '\n\nASFI Research Journal\nExcellence. Quality. Impact\n"Raising the bar of scientific publishing in Africa"\n' },
    { attributes: { link: 'https://asfirj.org/' }, insert: 'https://asfirj.org/' },
    { insert: '\n' },
    { attributes: { link: 'mailto:submissions@asfirj.org' }, insert: 'submissions@asfirj.org' },
    { insert: '\nLinkedIn: ' },
    { attributes: { link: 'www.linkedin.com/in/asfi-research-journal-1b9929309' }, insert: 'www.linkedin.com/in/asfi-research-journal-1b9929309' },
    { insert: '\nX (formerly Twitter): ' },
    { attributes: { link: 'https://twitter.com/asfirj1' }, insert: 'https://twitter.com/asfirj1' },
    { insert: '\nInstagram: ' },
    { attributes: { link: ' https://www.instagram.com/asfirj1/' }, insert: ' https://www.instagram.com/asfirj1/' },
    { insert: '\nWhatsApp: ' },
    { attributes: { link: 'https://chat.whatsapp.com/L8o0N0pUieOGIUHJ1hjSG3' }, insert: 'https://chat.whatsapp.com/L8o0N0pUieOGIUHJ1hjSG3' },
    { insert: '\n\n\nThe full comments of the reviewers are reproduced below:\n' },
    { attributes: { color: '#e60000', bold: true }, insert: "[Enter Reviewer's Comments]" },
    { insert: '\n' }
  ],
  return: ({ manuscriptId, title, correspondingAuthor, today }) => [
    { insert: `${today}\n\n` },
    { insert: 'Dear ' },
    { attributes: { color: '#e60000', bold: true }, insert: correspondingAuthor },
    { insert: `,\n\nYour manuscript "${title}" (${manuscriptId}) has been successfully submitted online and is presently being given full consideration for publication in African Science Frontiers Initiatives Research Journal (ASFIRJ).\n\nYour manuscript ID is ` },
    { attributes: { color: '#e60000', bold: true }, insert: manuscriptId },      { insert: `. Please mention the above manuscript ID in all future correspondence with ASFIRJ regarding this submission.\n\nYour paper has now been checked by the Editorial Office to ensure it adheres to ASFIRJ formats and standards, but your paper has several deviations from ASFIRJ requirements as stipulated in the authors' instructions (please carefully read the authors' instructions and ensure that all sections of the manuscript are in line with ASFIRJ requirements here: https://asfirj.org/authors.html).\nThe manuscript has now been returned to your author's dashboard.\n\nBefore the paper can be further processed and sent to a handling editor, we would like you to make the following under-listed amendments and resubmit the manuscript.\n\nAreas to amend in your manuscript:\n\n` },
    { attributes: { color: '#e60000', bold: true }, insert: '[Enter List of Areas of Manuscript To Amend]' },
    { insert: `\n\nSincerely,\n\nASFIRJ Editorial Office\nsubmissions@asfirj.org\n` }
  ],
  revise: ({ manuscriptId, title, correspondingAuthor, editorName, today, deadlineDate }) => [
    { insert: `${today}\n\n` },
    { insert: 'Dear ' },
    { attributes: { color: '#e60000', bold: true }, insert: correspondingAuthor },
    { insert: `,\n\nThank you for submitting your manuscript "${title}" (${manuscriptId}) to ASFI Research Journal (ASFIRJ). We have now received reviewers' reports on your paper and the reviewers' comments follow at the end of this email. Some of the reviewers also made direct comments to your manuscript and these are attached for your attention.\n\nThe reviewers have recommended a major/minor revision to your manuscript. The editors would be prepared to consider a revised version of the manuscript if you believe you can provide a full and convincing response to the criticisms the reviewers have raised. The revised manuscript will of course be sent back to the original reviewers for further assessment.\n\nAn opportunity to submit a revision of your manuscript does not guarantee acceptance since a revision may still not achieve a high enough priority to warrant publication.\n\nPlease study the comments carefully and amend the manuscript as appropriate. In your response, please ensure that you include all the comments by the reviewers and provide point-by-point reply to each point raised, specifying how you have revised your manuscript. Please attach the point-by-point response as a PDF file.\n\nIn addition to the point-by-point response to the reviewers' comments, we ask that you also upload a clean and marked copy of the revised manuscript. The marked copy should highlight the changes you have made using the 'Tracked Changes' function in Microsoft Word.\n\nPlease note that your paper will not be able to be processed without these three files uploaded (PDF of point-by-point response, clean copy of the revised manuscript, and marked copy of the revised manuscript).\n\nWhen ready with your revision, please upload all the files in your author's dashboard using the revise function aligned to the submitted manuscript.\n\nPlease do not submit your revised manuscript as a new submission.\n\nPlease ensure that the structure of the revised manuscript strictly conforms with ASFIRJ recommended manuscript structure and format. Please carefully consult the authors' instructions here: https://asfirj.org/authors.html.\n\nFailure to conform to ASFIRJ manuscript structure will cause a delay in publishing your paper if it is acceptable for publication in the journal as all formats have to be followed by all authors before publishing.\n\nYou have up to ` },
    { attributes: { color: '#e60000', bold: true }, insert: deadlineDate },
    { insert: ` to submit the revised manuscript.\n\nSincerely,\n\n` },
    { attributes: { color: '#e60000', bold: true }, insert: editorName },
    { insert: `\nAssociate Editor\n\nsubmissions@asfirj.org\n\nASFI Research Journal\nExcellence. Quality. Impact\n"Raising the bar of scientific publishing in Africa"\nhttps://asfirj.org/\nasfirj@asfirj.org\nLinkedIn: www.linkedin.com/in/asfi-research-journal-1b9929309\nX (formerly Twitter): https://twitter.com/asfirj1\nInstagram: https://www.instagram.com/asfirj1/\nWhatsApp: https://chat.whatsapp.com/L8o0N0pUieOGIUHJ1hjSG3\n` }
  ]
};

const DECISION_OPTIONS = [
  { value: 'accept', label: 'Accept', icon: ThumbsUp, description: 'Accept for publication' },
  { value: 'reject', label: 'Reject', icon: ThumbsDown, description: 'Reject the manuscript' },
  { value: 'return', label: 'Return for correction', icon: Undo2, description: 'Return for corrections' },
  { value: 'revise', label: 'Return for Revision', icon: RefreshCw, description: 'Request major/minor revision' }
];

const DECISION_SUBJECTS = {
  accept: (title, id) => `Acceptance of Your Manuscript: ${title} (${id})`,
  reject: (title, id) => `Decision on Your Manuscript: ${title} (${id})`,
  return: (title, id) => `Returned for Correction: ${title} (${id})`,
  revise: (title, id) => `Request for Revision: ${title} (${id})`
};

const escapeHtml = (str) =>
  String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const deltaToHtml = (delta) => {
  const joined = delta
    .map((op) => {
      const attrs = op.attributes || {};
      let inner = escapeHtml(op.insert);
      if (attrs.bold) inner = `<strong>${inner}</strong>`;
      if (attrs.color) inner = `<span style="color:${attrs.color}">${inner}</span>`;
      if (attrs.link) inner = `<a href="${escapeHtml(attrs.link)}">${inner}</a>`;
      return inner;
    })
    .join('');
  return joined
    .split('\n\n')
    .map((p) => `<p>${p.replace(/\n/g, '<br>')}</p>`)
    .join('');
};

const stripHtml = (html) => {
  const div = document.createElement('div');
  div.innerHTML = html || '';
  return (div.textContent || '').trim();
};

const getCorrespondingAuthorName = (submission, authors) => {
  const email = submission?.corresponding_authors_email;
  const match = (authors || []).find((a) => (a.authors_email || a.email) === email);
  if (match) {
    return (
      match.authors_fullname ||
      [match.firstname, match.lastname].filter(Boolean).join(' ').trim() ||
      email ||
      'Author'
    );
  }
  return email || 'Author';
};

const DecisionForm = () => {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const [decisionType, setDecisionType] = useState('');
  const [subject, setSubject] = useState('');
  const [compiledLetter, setCompiledLetter] = useState('');
  const [ccEmail, setCcEmail] = useState('');
  const [bccEmail, setBccEmail] = useState('');
  const [ccTags, setCcTags] = useState([]);
  const [ccInput, setCcInput] = useState('');
  const [bccTags, setBccTags] = useState([]);
  const [bccInput, setBccInput] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [attachmentPreview, setAttachmentPreview] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Editor invite state
  const [showEditorInvite, setShowEditorInvite] = useState(false);
  const [editorInviteEmail, setEditorInviteEmail] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [editorSuggestions, setEditorSuggestions] = useState([]);
  const [filteredEditorSuggestions, setFilteredEditorSuggestions] = useState([]);
  const [showEditorSuggestions, setShowEditorSuggestions] = useState(false);
  const [editorSuggestionsLoading, setEditorSuggestionsLoading] = useState(false);

  useEffect(() => {
    const fetchDecisionData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`/editors/backend/editors/decision-data/${articleId}`);
        if (response.data.status === 'success') {
          setData(response.data.data);
          setSubject('');
          setCompiledLetter('');
        } else {
          showToast(response.data.message || 'Failed to load decision data', 'error');
          navigate('/editors/submitted-reviews');
        }
      } catch (error) {
        console.error('Error fetching decision data:', error);
        showToast(error.response?.data?.message || 'Failed to load decision data. Please try again.', 'error');
        navigate('/editors/submitted-reviews');
      } finally {
        setLoading(false);
      }
    };

    if (articleId) {
      fetchDecisionData();
    }
  }, [articleId, navigate]);

  // Fetch editor email suggestions
  useEffect(() => {
    const fetchEditorSuggestions = async () => {
      setEditorSuggestionsLoading(true);
      try {
        const response = await axios.post('/editors/listOfEditorEmails', { articleId });
        if (response.data.success && response.data.emails) {
          setEditorSuggestions(response.data.emails);
        }
      } catch (error) {
        console.error('Error fetching editor email suggestions:', error);
      } finally {
        setEditorSuggestionsLoading(false);
      }
    };
    if (articleId) fetchEditorSuggestions();
  }, [articleId]);

  // Filter editor suggestions based on input
  useEffect(() => {
    if (editorInviteEmail && editorInviteEmail.length > 0) {
      const filtered = editorSuggestions.filter(email =>
        email.toLowerCase().includes(editorInviteEmail.toLowerCase())
      );
      setFilteredEditorSuggestions(filtered);
      setShowEditorSuggestions(filtered.length > 0);
    } else {
      setFilteredEditorSuggestions([]);
      setShowEditorSuggestions(false);
    }
  }, [editorInviteEmail, editorSuggestions]);

  const handleSelectDecision = (type) => {
    if (!data) return;
    const { submission } = data;
    const today = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    const deadlineDays = type === 'revise' ? 28 : 0;
    let deadlineDate = '';
    if (deadlineDays) {
      const d = new Date();
      d.setDate(d.getDate() + deadlineDays);
      deadlineDate = d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
    const editorName = user?.fullname || 'ASFI Editorial Office';
    const editorDesignation =
      user?.designation || (type === 'accept' ? 'Editor-in-Chief' : 'Associate Editor');

    const delta = DECISION_TEMPLATES[type]({
      manuscriptId: submission.revision_id,
      title: submission.title,
      correspondingAuthor: getCorrespondingAuthorName(submission, data.authors),
      editorName,
      editorDesignation,
      today,
      deadlineDate
    });

    setDecisionType(type);
    setSubject(DECISION_SUBJECTS[type](submission.title, submission.revision_id));
    setCompiledLetter(deltaToHtml(delta));
  };



  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleCcKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      addCcEmail();
    } else if (e.key === 'Backspace' && ccInput === '' && ccTags.length > 0) {
      removeCcTag(ccTags.length - 1);
    }
  };

  const handleCcBlur = () => {
    if (ccInput.trim()) addCcEmail();
  };

  const addCcEmail = () => {
    const email = ccInput.trim().replace(/,/g, '');
    if (email && isValidEmail(email) && !ccTags.includes(email)) {
      const newTags = [...ccTags, email];
      setCcTags(newTags);
      setCcEmail(newTags.join(', '));
      setCcInput('');
    }
  };

  const removeCcTag = (indexToRemove) => {
    const newTags = ccTags.filter((_, index) => index !== indexToRemove);
    setCcTags(newTags);
    setCcEmail(newTags.join(', '));
  };

  const handleBccKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      addBccEmail();
    } else if (e.key === 'Backspace' && bccInput === '' && bccTags.length > 0) {
      removeBccTag(bccTags.length - 1);
    }
  };

  const handleBccBlur = () => {
    if (bccInput.trim()) addBccEmail();
  };

  const addBccEmail = () => {
    const email = bccInput.trim().replace(/,/g, '');
    if (email && isValidEmail(email) && !bccTags.includes(email)) {
      const newTags = [...bccTags, email];
      setBccTags(newTags);
      setBccEmail(newTags.join(', '));
      setBccInput('');
    }
  };

  const removeBccTag = (indexToRemove) => {
    const newTags = bccTags.filter((_, index) => index !== indexToRemove);
    setBccTags(newTags);
    setBccEmail(newTags.join(', '));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (attachments.length + files.length > 10) {
      showToast('Maximum 10 files allowed', 'error');
      return;
    }
    setAttachments(prev => [...prev, ...files]);
    const newPreviews = files.map(file => ({
      name: file.name,
      size: (file.size / 1024).toFixed(2),
      url: URL.createObjectURL(file)
    }));
    setAttachmentPreview(prev => [...prev, ...newPreviews]);
    e.target.value = '';
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
    setAttachmentPreview(prev => prev.filter((_, i) => i !== index));
  };

  const handleInviteEditor = async () => {
    if (!editorInviteEmail.trim()) {
      showToast('Please enter an editor email', 'error');
      return;
    }
    setShowEditorSuggestions(false);
    setSendingInvite(true);
    try {
      const response = await axios.post('/editors/backend/editors/invite-editor-decision', {
        articleId,
        editorEmail: editorInviteEmail.trim(),
      });
      if (response.data.status === 'success' || response.data.status === 'warning') {
        showToast(response.data.message, response.data.status === 'warning' ? 'info' : 'success');
        setEditorInviteEmail('');
        setShowEditorInvite(false);
      } else {
        showToast(response.data.message || 'Failed to send invite', 'error');
      }
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to send invite. Please try again.', 'error');
    } finally {
      setSendingInvite(false);
    }
  };

  const handleEditorInviteSuggestionClick = (email) => {
    setEditorInviteEmail(email);
    setShowEditorSuggestions(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!decisionType) {
      showToast('Please select a decision type', 'error');
      return;
    }
    if (!subject.trim()) {
      showToast('Please enter an email subject', 'error');
      return;
    }
    if (!compiledLetter.trim()) {
      showToast('Please compile the decision letter', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('articleId', articleId);
      formDataToSend.append('decisionType', decisionType);
      formDataToSend.append('compiledLetter', compiledLetter);
      formDataToSend.append('subject', subject.trim());
      formDataToSend.append('message', stripHtml(compiledLetter) || subject.trim());
      formDataToSend.append('ccEmail', ccEmail);
      formDataToSend.append('bccEmail', bccEmail);

      attachments.forEach(file => {
        formDataToSend.append('attachments[]', file);
      });

      const response = await axios.post('/editors/backend/editors/submit-decision', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60000,
      });

      if (response.data.status === 'success') {
        showToast(response.data.message, 'success');
        navigate('/editors/submitted-reviews');
      } else {
        showToast(response.data.message || 'Failed to submit decision', 'error');
      }
    } catch (error) {
      console.error('Error submitting decision:', error);
      showToast(error.response?.data?.message || 'Failed to submit decision. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading manuscript and reviewer reports...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center text-gray-600">No data available</div>
      </div>
    );
  }

  const { submission, reviews, authors, keywords } = data;

  const submissionFiles = Object.entries(FILE_LABELS).filter(
    ([key]) => submission[key]
  );

  return (
    <div className="container-fluid px-4 py-6">
      <div className="mb-6">
        <Link
          to="/editors/submitted-reviews"
          className="inline-flex items-center text-sm text-[--asfi-purple] hover:text-[--asfi-blue] mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Submitted Reviews
        </Link>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center">
              <ClipboardCheck className="w-6 h-6 mr-3 text-[--asfi-purple]" />
              Editorial Decision
            </h1>
            <p className="text-gray-600 mt-1">
              {submission.revision_id} · {submission.title}
            </p>
          </div>
          <span className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-full border bg-purple-100 text-purple-800 border-purple-200">
            {submission.status?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: manuscript details */}
        <div className="lg:col-span-1 space-y-6">
          {/* Manuscript details */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Manuscript Details</h3>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-xs text-gray-500 uppercase">Article Type</dt>
                <dd className="text-gray-900">{submission.article_type || 'N/A'}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500 uppercase">Discipline</dt>
                <dd className="text-gray-900">{submission.discipline || 'N/A'}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500 uppercase">Submitted</dt>
                <dd className="text-gray-900">{new Date(submission.date_submitted || submission.process_start_date).toLocaleDateString()}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500 uppercase">Corresponding Author</dt>
                <dd className="text-gray-900 break-all">{submission.corresponding_authors_email || 'N/A'}</dd>
              </div>
            </dl>

            {authors && authors.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Users className="w-4 h-4 mr-2 text-gray-400" />
                  Authors
                </h4>
                <ul className="space-y-2">
                  {authors.map((author, index) => (
                    <li key={index} className="text-sm text-gray-700 flex items-start">
                      <User className="w-4 h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium">
                          {author.authors_fullname || `${author.firstname || ''} ${author.lastname || ''}`.trim()}
                        </p>
                        <p className="text-xs text-gray-500">{author.authors_email || author.email}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {keywords && keywords.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Tag className="w-4 h-4 mr-2 text-gray-400" />
                  Keywords
                </h4>
                <div className="flex flex-wrap gap-2">
                  {keywords.map((keyword, index) => (
                    <span key={index} className="px-2.5 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Abstract */}
          {submission.abstract && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Abstract</h3>
              <div className="prose max-w-none text-sm text-gray-700">
                <RichTextViewer content={submission.abstract} />
              </div>
            </div>
          )}

          {/* Files */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <Paperclip className="w-5 h-5 mr-2 text-gray-400" />
              Manuscript Files
            </h3>
            <div className="space-y-2">
              {submissionFiles.map(([key, label]) => (
                <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center min-w-0">
                    <FileText className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                    <span className="text-sm text-gray-700 truncate">{label}</span>
                  </div>
                  <button
                    onClick={() => handleDownloadFile(submission[key], `${submission.revision_id}_${label.replace(/\s+/g, '_')}`)}
                    className="p-1 text-gray-500 hover:text-[--asfi-purple] hover:bg-white rounded transition-colors flex-shrink-0"
                    title={`Download ${label}`}
                  >
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {submissionFiles.length === 0 && (
                <p className="text-sm text-gray-500">No manuscript files available.</p>
              )}
            </div>
          </div>
        </div>

        {/* Right column: reviews + decision form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Reviewer Reports */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Reviewer Reports ({reviews.length})
              </h3>
            </div>

            {reviews.length === 0 ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start">
                <AlertTriangle className="w-5 h-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-amber-800">
                  No reviewer reports have been submitted for this manuscript yet.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {reviews.map((review) => {
                  const section1Total = sumScores(review, SECTION1_SCORES);
                  const section2Total = sumScores(review, SECTION2_SCORES);
                  return (
                    <div key={review.id} className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="p-4 bg-gray-50 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div>
                          <p className="font-semibold text-gray-900">{review.reviewer_name}</p>
                          <p className="text-xs text-gray-500">{review.reviewer_email}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${getRecommendationColor(review.overall_recommendation)}`}>
                            {review.overall_recommendation || 'Not specified'}
                          </span>
                          <Link
                            to={`/editors/review/content/${review.review_id}/${articleId}`}
                            className="inline-flex items-center px-3 py-1.5 text-xs font-medium border border-gray-300 text-gray-700 rounded-lg hover:bg-white transition-colors"
                          >
                            <Eye className="w-3.5 h-3.5 mr-1" />
                            Full Report
                          </Link>
                        </div>
                      </div>

                      <div className="p-4 space-y-4">
                        {/* Scores */}
                        <div className="grid grid-cols-3 gap-3">
                          <div className="bg-purple-50 rounded-lg p-3 text-center">
                            <p className="text-xs text-gray-500">Section 1 Score</p>
                            <p className="text-2xl font-bold text-purple-700">{section1Total}</p>
                            <p className="text-xs text-gray-400">out of {SECTION1_SCORES.length * 5}</p>
                          </div>
                          <div className="bg-purple-50 rounded-lg p-3 text-center">
                            <p className="text-xs text-gray-500">Section 2 Score</p>
                            <p className="text-2xl font-bold text-purple-700">{section2Total}</p>
                            <p className="text-xs text-gray-400">out of {SECTION2_SCORES.length * 5}</p>
                          </div>
                          <div className="bg-purple-50 rounded-lg p-3 text-center">
                            <p className="text-xs text-gray-500">Total Score</p>
                            <p className="text-2xl font-bold text-purple-700">{section1Total + section2Total}</p>
                            <p className="text-xs text-gray-400">out of {(SECTION1_SCORES.length + SECTION2_SCORES.length) * 5}</p>
                          </div>
                        </div>

                        {/* Comments */}
                        <div className="space-y-3">
                          {review.one_paragraph_comment && (
                            <div>
                              <p className="text-xs font-medium text-gray-500 uppercase mb-1">Summary of Manuscript</p>
                              <div className="prose max-w-none text-sm text-gray-700">
                                <RichTextViewer content={review.one_paragraph_comment} />
                              </div>
                            </div>
                          )}
                          {review.general_comment && (
                            <div>
                              <p className="text-xs font-medium text-gray-500 uppercase mb-1">General Comments</p>
                              <div className="prose max-w-none text-sm text-gray-700">
                                <RichTextViewer content={review.general_comment} />
                              </div>
                            </div>
                          )}
                          {review.specific_comment && (
                            <div>
                              <p className="text-xs font-medium text-gray-500 uppercase mb-1">Specific Comments</p>
                              <div className="prose max-w-none text-sm text-gray-700">
                                <RichTextViewer content={review.specific_comment} />
                              </div>
                            </div>
                          )}
                          {review.letter_to_editor && (
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                              <p className="text-xs font-medium text-gray-500 uppercase mb-1">Letter to the Editor</p>
                              <div className="prose max-w-none text-sm text-gray-700">
                                <RichTextViewer content={review.letter_to_editor} />
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Review files */}
                        {[['one_paragraph_file', 'Summary File'], ['general_comment_file', 'General Comments File'], ['specific_comment_file', 'Specific Comments File']]
                          .filter(([key]) => review[key])
                          .length > 0 && (
                          <div className="space-y-2">
                            {[['one_paragraph_file', 'Summary File'], ['general_comment_file', 'General Comments File'], ['specific_comment_file', 'Specific Comments File']]
                              .filter(([key]) => review[key])
                              .map(([key, label]) => (
                                <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                  <div className="flex items-center min-w-0">
                                    <FileText className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                                    <span className="text-sm text-gray-700 truncate">{label}</span>
                                  </div>
                                  <button
                                    onClick={() => handleDownloadFile(review[key], `${articleId}_${review.review_id}_${key}`)}
                                    className="p-1 text-gray-500 hover:text-[--asfi-purple] hover:bg-white rounded transition-colors flex-shrink-0"
                                    title={`Download ${label}`}
                                  >
                                    <Download className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Decision Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Compile Decision Letter to Authors
            </h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Decision <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {DECISION_OPTIONS.map((option) => {
                  const Icon = option.icon;
                  const isSelected = decisionType === option.value;
                  return (
                    <button
                      type="button"
                      key={option.value}
                      onClick={() => handleSelectDecision(option.value)}
                      className={`flex items-start gap-3 p-4 rounded-lg border text-left transition-all ${
                        isSelected
                          ? 'border-[--asfi-purple] bg-purple-50 ring-2 ring-purple-200'
                          : 'border-gray-200 hover:border-[--asfi-purple] hover:bg-purple-50/50'
                      }`}
                    >
                      <Icon
                        className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                          isSelected ? 'text-[--asfi-purple]' : 'text-gray-400'
                        }`}
                      />
                      <div>
                        <p className="font-medium text-gray-900">{option.label}</p>
                        <p className="text-xs text-gray-500">{option.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Selecting a decision loads the matching email template below.
              </p>
            </div>

            {!decisionType && (
              <div className="mb-4">
                {!showEditorInvite ? (
                  <button
                    type="button"
                    onClick={() => setShowEditorInvite(true)}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-[--asfi-purple] border border-[--asfi-purple] rounded-lg hover:bg-purple-50 transition-colors"
                  >
                    <UserPlus className="w-4 h-4" />
                    Invite Handling Editor
                  </button>
                ) : (
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-400" />
                      Invite a handling editor to make the decision
                    </p>
                    <div className="relative">
                      <input
                        type="email"
                        value={editorInviteEmail}
                        onChange={(e) => setEditorInviteEmail(e.target.value)}
                        onFocus={() => filteredEditorSuggestions.length > 0 && setShowEditorSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowEditorSuggestions(false), 200)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleInviteEditor();
                          }
                        }}
                        placeholder="editor@example.com"
                        autoComplete="off"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent text-sm pr-10"
                      />
                      {editorSuggestionsLoading && (
                        <div className="absolute right-3 top-2.5">
                          <Loader className="w-4 h-4 animate-spin text-purple-600" />
                        </div>
                      )}
                      {!editorSuggestionsLoading && editorSuggestions.length > 0 && (
                        <Search className="absolute right-3 top-2.5 w-4 h-4 text-gray-400" />
                      )}
                      {showEditorSuggestions && filteredEditorSuggestions.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {filteredEditorSuggestions.map((email, index) => (
                            <button
                              key={index}
                              type="button"
                              className="w-full text-left px-4 py-2 hover:bg-purple-50 focus:bg-purple-50 focus:outline-none border-b border-gray-100 last:border-0"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => handleEditorInviteSuggestionClick(email)}
                            >
                              <span className="text-sm text-gray-700">{email}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 mt-2">
                      <button
                        type="button"
                        onClick={handleInviteEditor}
                        disabled={sendingInvite}
                        className="px-4 py-2 bg-[--asfi-blue] text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                      >
                        {sendingInvite ? (
                          <Loader className="w-4 h-4 animate-spin" />
                        ) : (
                          <UserPlus className="w-4 h-4" />
                        )}
                        {sendingInvite ? 'Sending...' : 'Send Invite'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowEditorInvite(false); setEditorInviteEmail(''); setShowEditorSuggestions(false); }}
                        className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {decisionType && (
              <>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Subject <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent text-sm"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Decision Letter <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-gray-500 mb-2">
                The template for the selected decision has been loaded below. Edit it as needed
                before sending.
              </p>
              <RichTextEditor
                value={compiledLetter}
                onChange={setCompiledLetter}
                onImageExtracted={uploadEditorImage}
                placeholder="Compile the decision letter referencing the reviewer reports above..."
                minHeight="200px"
                maxHeight="350px"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CC
                </label>
                <div className="flex flex-wrap items-center gap-2 p-2 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-[--asfi-purple] focus-within:border-transparent min-h-[42px]">
                  {ccTags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-md text-sm bg-blue-100 text-blue-700"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeCcTag(index)}
                        className="ml-1 hover:text-blue-900"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    className="flex-1 min-w-[200px] outline-none border-0 p-1 text-sm"
                    value={ccInput}
                    onChange={(e) => setCcInput(e.target.value)}
                    onKeyDown={handleCcKeyDown}
                    onBlur={handleCcBlur}
                    placeholder={ccTags.length === 0 ? "Enter CC emails (comma separated)" : ""}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  BCC
                </label>
                <div className="flex flex-wrap items-center gap-2 p-2 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-[--asfi-purple] focus-within:border-transparent min-h-[42px]">
                  {bccTags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-md text-sm bg-gray-100 text-gray-700"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeBccTag(index)}
                        className="ml-1 hover:text-gray-900"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    className="flex-1 min-w-[200px] outline-none border-0 p-1 text-sm"
                    value={bccInput}
                    onChange={(e) => setBccInput(e.target.value)}
                    onKeyDown={handleBccKeyDown}
                    onBlur={handleBccBlur}
                    placeholder={bccTags.length === 0 ? "Enter BCC emails (comma separated)" : ""}
                  />
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              * Press Enter, Space, or Comma to add email addresses
            </p>

            {/* Attachments Section */}
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Attachments (Max: 10 Files)
              </h4>
              <input
                type="file"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent text-sm"
                name="attachments[]"
                id="attachmentInput"
                multiple
                onChange={handleFileChange}
                accept="*/*"
              />
              {attachmentPreview.length > 0 && (
                <div className="mt-4 space-y-2">
                  {attachmentPreview.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center min-w-0">
                        <Paperclip className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-700 truncate">{file.name}</p>
                          <p className="text-xs text-gray-500">{file.size} KB</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAttachment(index)}
                        className="p-1 text-gray-400 hover:text-red-500 hover:bg-white rounded flex-shrink-0"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => navigate('/editors/submitted-reviews')}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2 bg-[--asfi-blue] text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <ClipboardCheck className="w-4 h-4" />
                    Submit Decision
                  </>
                )}
              </button>
            </div>
              </>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default DecisionForm;
